"use client";

import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { cn } from "@/utils";

type BlobItem = { el: HTMLElement; s: number; x: number; y: number };

const items = new Set<BlobItem>();
let started = false;
let mouseX = 0;
let mouseY = 0;

const onMouseMove = (e: MouseEvent) => {
  mouseX = e.clientX / window.innerWidth - 0.5;
  mouseY = e.clientY / window.innerHeight - 0.5;
};

// Single rAF loop shared by every blob: scroll parallax + mouse drift, lerped.
const tick = () => {
  items.forEach((b) => {
    const tx = mouseX * 700 * b.s;
    const ty = window.scrollY * b.s + mouseY * 700 * b.s;
    b.x += (tx - b.x) * 0.055;
    b.y += (ty - b.y) * 0.055;
    b.el.style.transform = `translate3d(${b.x.toFixed(2)}px,${b.y.toFixed(2)}px,0)`;
  });
  requestAnimationFrame(tick);
};

const start = () => {
  if (started) return;
  started = true;
  window.addEventListener("mousemove", onMouseMove, { passive: true });
  requestAnimationFrame(tick);
};

type BlobProps = {
  /** parallax factor — 0 disables motion tracking */
  speed?: number;
  className?: string;
  style?: CSSProperties;
};

const Blob = ({ speed = 0, className, style }: BlobProps) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !speed) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const item: BlobItem = { el, s: speed, x: 0, y: 0 };
    items.add(item);
    start();

    return () => {
      items.delete(item);
    };
  }, [speed]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      style={style}
      className={cn(
        "jlt-pointer-events-none jlt-absolute jlt-z-0 jlt-rounded-blob jlt-blur-[2px] jlt-will-change-transform",
        "jlt-animate-blob-float motion-reduce:jlt-animate-none",
        className
      )}
    />
  );
};

export default Blob;
